import { setType } from '@/common/utils/typedValueSerializer'
import { unwrapScriptValue } from '@/common/utils/wrapScriptValue'
import UniDataTable from './UniDataTable'
import UniDate from './UniDate'

interface TypedValue {
  value: any
  type?: string
}

/** Swing UniDataTableFields.getFieldAsString: Boolean → "1" / "0" */
function valueAsFieldString(value: unknown, def: string = ''): string {
  if (value == null) return def
  if (typeof value === 'boolean') return value ? '1' : '0'
  return String(value)
}

/**
 * Создает объект-обертку с методами getField, getFieldAsString и т.д. для доступа к полям объекта
 */
function createFieldAccessor(fields: Record<string, TypedValue | any>): any {
  const getFieldValue = (fieldName: string): any => {
    const field = fields[fieldName]
    if (field == null) {
      return undefined
    }
    // Если поле в формате TypedValue {value, type}
    if (typeof field === 'object' && 'value' in field) {
      return field.value
    }
    // Если поле - это обычное значение
    return field
  }

  return {
    getField(fieldName: string): any {
      const field = fields[fieldName]
      if (field == null) {
        return undefined
      }
      // Если поле в формате TypedValue, возвращаем значение
      if (typeof field === 'object' && ('value' in field || 'type' in field)) {
        return (field as TypedValue).value
      }
      // Иначе возвращаем значение напрямую
      return field
    },
    getFieldAsString(fieldName: string, def: string = ''): string {
      return valueAsFieldString(getFieldValue(fieldName), def)
    },
    getFieldAsLong(fieldName: string, def: number = 0): number {
      const value = getFieldValue(fieldName)
      const num = value != null ? Number(value) : NaN
      return isNaN(num) ? def : num
    },
    getFieldAsBoolean(fieldName: string, def: boolean = false): boolean {
      const value = getFieldValue(fieldName)
      return value != null ? Boolean(value) : def
    },
    getFieldAsDouble(fieldName: string, def: number = 0): number {
      const value = getFieldValue(fieldName)
      if (value == null || value === '') return def
      const n = Number(value)
      return Number.isFinite(n) ? n : def
    },
    hasField(fieldName: string): boolean {
      return Object.prototype.hasOwnProperty.call(fields, fieldName)
    },
    hasTable(fieldName: string): boolean {
      const f = fields[fieldName]
      if (f == null) return false
      const typed = f as TypedValue & { type?: string }
      if (typed.type === 'UniDataTable' || typed.type === 'UniDataTableFields') return true
      const v = 'value' in typed ? typed.value : typed
      if (v instanceof UniDataTable) return true
      if (v && typeof v === 'object' && Array.isArray((v as { rows?: unknown }).rows)) return true
      return false
    },
    getFieldAsObject(fieldName: string): any {
      return getFieldValue(fieldName)
    },
    getFieldAsByteArray(fieldName: string, def: Uint8Array | null = null): Uint8Array | null {
      // Поддержка вложенных полей через точку
      const parts = fieldName.split('.')
      let currentValue: any = getFieldValue(parts[0])

      // Проходим по вложенным полям
      for (let i = 1; i < parts.length; i++) {
        if (currentValue == null) {
          return def
        }

        if (typeof currentValue === 'object' && currentValue.fields) {
          const field = currentValue.fields[parts[i]]
          currentValue = field?.value ?? field
        } else if (typeof currentValue === 'object') {
          currentValue = currentValue[parts[i]]
        } else {
          return def
        }
      }

      if (currentValue == null) {
        return def
      }

      // Преобразуем в Uint8Array
      if (currentValue instanceof Uint8Array) {
        return currentValue
      }
      if (currentValue instanceof ArrayBuffer) {
        return new Uint8Array(currentValue)
      }
      if (Array.isArray(currentValue)) {
        return new Uint8Array(currentValue)
      }
      if (typeof currentValue === 'string') {
        try {
          const binary = atob(currentValue)
          const bytes = new Uint8Array(binary.length)
          for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i)
          }
          return bytes
        } catch {
          const bytes = new Uint8Array(currentValue.length)
          for (let i = 0; i < currentValue.length; i++) {
            bytes[i] = currentValue.charCodeAt(i)
          }
          return bytes
        }
      }

      return def
    },
    // Сохраняем доступ к исходным полям для совместимости
    ...fields,
  }
}

interface RowData {
  fields?: Record<string, TypedValue>
  attributes?: Record<string, any>
  errors?: Record<string, any>
}

/**
 * Отдельная строка UniDataTable
 */
export default class UniDataTableRow {
  fields: Record<string, TypedValue>
  attributes: Record<string, any>
  errors: Record<string, any>
  idx: number
  private _setField: (fieldName: string, value: any, rowIdx: number) => void

  constructor(row: RowData = {}, idx: number = 0, setField: (fieldName: string, value: any, rowIdx: number) => void = () => { }) {
    if (row.fields && typeof row.fields === 'object') {
      const f = row.fields as Record<string, any>
      if (
        f.fields != null &&
        typeof f.fields === 'object' &&
        !Array.isArray(f.fields)
      ) {
        this.fields = f.fields
      } else {
        this.fields = f
      }
    } else if (row && typeof row === 'object' && !Array.isArray(row)) {
      // Flat row: { title: "x", main: 1 } -> { title: { value: "x" }, main: { value: 1 } }
      this.fields = Object.fromEntries(
        Object.entries(row)
          .filter(([k]) => k !== 'attributes' && k !== 'errors')
          .map(([k, v]) => [k, v != null && typeof v === 'object' && 'value' in v ? v : { value: v }])
      )
    } else {
      this.fields = {}
    }
    this.attributes = row.attributes ?? {}
    this.errors = row.errors ?? {}
    this.idx = idx
    this._setField = setField
  }

  /**
   * Устанавливает значение поля данной строки в исходной таблице
   * Тип определяется автоматически по содержимому (setType).
   */
  setField(fieldName: string, value: any): this {
    this.fields[fieldName] = setType(value)
    this._setField(fieldName, value, this.idx)
    return this
  }

  /**
   * Устанавливает поле с явным указанием типа.
   * Используется когда необходимо задать тип независимо от значения (например, Long вместо Integer, String для null).
   * @param fieldName - имя поля
   * @param value - значение
   * @param type - тип (String, Long, Integer, Boolean, Date, UniDataTable и т.д.)
   */
  setFieldWithType(fieldName: string, value: any, type: string): this {
    this.fields[fieldName] = { value, type }
    this._setField(fieldName, { value, type }, this.idx)
    return this
  }

  /**
   * alias for setField
   */
  setFieldAsObject(fieldName: string, value: any): this {
    return this.setField(fieldName, value)
  }

  getField(name: string): TypedValue | any {
    if (this.fields[name] === undefined) {
      console.warn(`UniData: field ${name} not found`)
      return { value: undefined }
    }
    const tv = this.fields[name]
    const v = tv.value
    if (
      v != null &&
      typeof v === 'object' &&
      !Array.isArray(v) &&
      !(v instanceof UniDataTable) &&
      (v as { fields?: unknown }).fields != null &&
      typeof (v as { fields?: unknown }).fields === 'object' &&
      !Array.isArray((v as { fields?: unknown }).fields)
    ) {
      return createFieldAccessor((v as { fields: Record<string, TypedValue | any> }).fields)
    }
    if (tv && typeof tv === 'object' && 'value' in tv) {
      if (v instanceof UniDataTable) {
        return v
      }
      return v
    }
    return tv
  }

  private resolveGetFieldValue(name: string): unknown {
    const raw = this.getField(name)
    if (raw != null && typeof raw === 'object' && 'value' in (raw as Record<string, unknown>)) {
      return (raw as { value?: unknown }).value
    }
    return raw
  }

  getFieldAsString(name: string, def: string = ''): string {
    if (!this.fields[name]) return def
    return valueAsFieldString(this.resolveGetFieldValue(name), def)
  }

  getFieldAsLong(name: string, def: number = 0): number {
    if (!this.fields[name]) return def
    const value = this.resolveGetFieldValue(name)
    const num = value != null ? Number(value) : NaN
    return Number.isFinite(num) ? num : def
  }

  getFieldAsDouble(name: string, def: number = 0): number {
    if (!this.fields[name]) return def
    const value = this.resolveGetFieldValue(name)
    if (value == null || value === '') return def
    const n = Number(value)
    return Number.isFinite(n) ? n : def
  }

  getFieldAsBoolean(name: string, def: boolean = false): boolean {
    if (!this.fields[name]) return def
    const value = this.resolveGetFieldValue(name)
    if (value == null) return def
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value !== 0
    const normalized = String(value).trim().toLowerCase()
    if (normalized === '0' || normalized === 'false') return false
    if (normalized === '1' || normalized === 'true') return true
    return Boolean(value)
  }

  getFieldAsDate(name: string, def: UniDate | null = null): UniDate | null {
    if (!this.hasField(name)) {
      return def
    }
    const value = this.resolveGetFieldValue(name)
    if (value == null || value === '') {
      return def
    }
    let dateInput: string | Date
    if (value instanceof Date) {
      dateInput = value
    } else if (typeof value === 'string') {
      dateInput = value
    } else if (typeof value === 'number') {
      dateInput = String(value)
    } else {
      return def
    }
    const date = new UniDate(dateInput)
    return date.isNull ? def : date
  }

  getFieldAsTable(name: string, def: UniDataTable | null = null): UniDataTable {
    if (!this.hasField(name)) {
      return def != null ? (def instanceof UniDataTable ? def : new UniDataTable(def as any)) : new UniDataTable()
    }
    const inner = this.fields[name]?.value
    if (inner == null || inner === undefined) {
      return def != null ? (def instanceof UniDataTable ? def : new UniDataTable(def as any)) : new UniDataTable()
    }
    return new UniDataTable(inner)
  }

  /**
   * Слияние полей из другой строки UniData-подобного объекта
   */
  add(data: any, _mod?: number): this {
    if (data == null) return this
    if (data instanceof UniDataTableRow) {
      for (const key of Object.keys(data.fields ?? {})) {
        const cell = data.fields[key]
        this.setField(key, cell?.value !== undefined ? cell.value : cell)
      }
      return this
    }
    if (data && typeof data === 'object' && typeof (data as { _getAllFields?: () => unknown })._getAllFields === 'function') {
      const map = (data as { _getAllFields: () => Record<string, TypedValue> })._getAllFields()
      for (const key of Object.keys(map ?? {})) {
        const cell = map[key]
        this.setField(key, cell && typeof cell === 'object' && 'value' in cell ? (cell as TypedValue).value : cell)
      }
      return this
    }
    if (typeof data === 'object' && data.fields && typeof data.fields === 'object') {
      for (const key of Object.keys(data.fields)) {
        const cell = data.fields[key]
        this.setField(key, cell && typeof cell === 'object' && 'value' in cell ? (cell as TypedValue).value : cell)
      }
      return this
    }
    return this
  }

  /**
   * Получает поле как массив байтов (Uint8Array).
   * Поддерживает вложенные поля через точку (например, "document.data").
   * @param name - имя поля (может содержать точку для вложенных полей)
   * @param def - значение по умолчанию, если поле не найдено
   * @returns Uint8Array или значение по умолчанию
   */
  getFieldAsByteArray(name: string, def: Uint8Array | null = null): Uint8Array | null {
    // Поддержка вложенных полей через точку
    const parts = name.split('.')
    let currentValue: any = this.getField(parts[0]).value

    // Проходим по вложенным полям
    for (let i = 1; i < parts.length; i++) {
      if (currentValue == null) {
        return def
      }

      // Если это объект с методами getField (например, UniDataTableRow)
      if (typeof currentValue === 'object' && typeof currentValue.getField === 'function') {
        currentValue = currentValue.getField(parts[i])
        if (currentValue && typeof currentValue === 'object' && 'value' in currentValue) {
          currentValue = currentValue.value
        }
      } else if (typeof currentValue === 'object' && currentValue.fields) {
        // Если это объект со структурой fields
        const field = currentValue.fields[parts[i]]
        currentValue = field?.value ?? field
      } else if (typeof currentValue === 'object') {
        // Обычный объект
        currentValue = currentValue[parts[i]]
      } else {
        return def
      }
    }

    if (currentValue == null) {
      return def
    }

    // Преобразуем в Uint8Array
    if (currentValue instanceof Uint8Array) {
      return currentValue
    }
    if (currentValue instanceof ArrayBuffer) {
      return new Uint8Array(currentValue)
    }
    if (Array.isArray(currentValue)) {
      return new Uint8Array(currentValue)
    }
    if (typeof currentValue === 'string') {
      // Предполагаем base64 строку
      try {
        const binary = atob(currentValue)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i)
        }
        return bytes
      } catch {
        // Если не base64, возвращаем как строку байтов
        const bytes = new Uint8Array(currentValue.length)
        for (let i = 0; i < currentValue.length; i++) {
          bytes[i] = currentValue.charCodeAt(i)
        }
        return bytes
      }
    }

    return def
  }

  /**
   * Получает поле как объект. Если значение является объектом со структурой fields,
   * возвращает обертку с методами getField, getFieldAsString и т.д.
   */
  getFieldAsObject(name: string): any {
    const tv = this.fields[name]
    const fieldValue = tv !== undefined ? tv.value : undefined

    // Если значение null или undefined, возвращаем как есть
    if (fieldValue == null) {
      return fieldValue
    }

    // Если значение уже имеет методы getField (например, UniDataTableRow или UniData), возвращаем как есть
    if (typeof fieldValue === 'object' && typeof fieldValue.getField === 'function') {
      return fieldValue
    }

    // Если значение - это объект со структурой fields (вложенный объект UniData)
    if (typeof fieldValue === 'object' && fieldValue.fields && typeof fieldValue.fields === 'object') {
      // Создаем обертку с методами для доступа к полям
      return createFieldAccessor(fieldValue.fields)
    }

    // Если значение - это обычный объект, но может содержать поля в формате TypedValue
    if (typeof fieldValue === 'object' && !Array.isArray(fieldValue)) {
      // Проверяем, есть ли хотя бы одно поле в формате TypedValue
      const hasTypedFields = Object.values(fieldValue).some(
        (v: any) => v && typeof v === 'object' && ('value' in v || 'type' in v)
      )

      if (hasTypedFields) {
        // Создаем обертку для доступа к полям
        return createFieldAccessor(fieldValue)
      }
    }

    // В остальных случаях возвращаем значение как есть
    return fieldValue
  }

  /**
   * Имена полей строки
   */
  fieldsArray(): string[] {
    return Object.keys(this.fields ?? {})
  }

  /**
   * Наличие поля в строке 
   */
  hasField(name: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.fields ?? {}, name)
  }

  /**
   * Поле строки — вложенная таблица 
   */
  hasTable(name: string): boolean {
    const f = this.fields?.[name]
    if (f == null) return false
    const typed = f as TypedValue & { type?: string }
    if (typed.type === 'UniDataTable' || typed.type === 'UniDataTableFields') return true
    const v = 'value' in typed ? typed.value : typed
    if (v instanceof UniDataTable) return true
    if (v && typeof v === 'object' && Array.isArray((v as { rows?: unknown }).rows)) return true
    return false
  }

  isTableField(name: string): boolean {
    return this.hasTable(name)
  }

  /**
   * Удаляет поле из строки 
   */
  removeField(fieldName: string): this {
    delete this.fields[fieldName]
    return this
  }

  /**
   * Копия строки для сборки результирующей таблицы
   */
  clone(_flags?: number): UniDataTableRow {
    const copiedFields: Record<string, TypedValue> = {}
    for (const key of Object.keys(this.fields ?? {})) {
      const tv = this.fields[key]
      if (tv != null && typeof tv === 'object' && 'value' in tv) {
        copiedFields[key] = {
          value: unwrapScriptValue(tv.value),
          ...(tv.type !== undefined ? { type: tv.type } : {}),
        }
      } else {
        copiedFields[key] = { value: tv as any }
      }
    }
    return new UniDataTableRow(
      { fields: copiedFields, attributes: { ...this.attributes }, errors: { ...this.errors } },
      0,
      () => {},
    )
  }

  /**
   * Устанавливает атрибут строки
   */
  setAttr(name: string, value: any): void {
    this.attributes[name] = value
  }

  /**
   * Конвертирует строку таблицы в JSON
   */
  toJSON(): RowData {
    return {
      fields: this.fields || {},
      errors: this.errors || {},
      attributes: this.attributes || {},
    }
  }
}
